import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import studyResourceStaffIssueService from '../../../services/StudyResourceStaffIssueService';
import ApiService from '../../../_services/ApiService';

interface Props {
  show: boolean;
  onHide: () => void;
  resourceId: number | null;
  resourceName: string;
}

type SchoolOption = { schoolId: number; schoolName: string };
type RoleOption = { roleId: number; roleName: string };
type UserOption = { userId: number; userName: string };

const StudyResourceStaffIssueModal: React.FC<Props> = ({
  show,
  onHide,
  resourceId,
  resourceName,
}) => {
  // Dropdown data
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);

  // Selected values
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | ''>('');
  const [selectedRoleId, setSelectedRoleId] = useState<number | ''>('');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  // Dates - default: today → 1 year from today
  const [visibleFrom, setVisibleFrom] = useState(() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  });
  const [visibleTo, setVisibleTo] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  });

  // Issue history
  const [issueHistory, setIssueHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // UI states
  const [loading, setLoading] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Load history function
  const loadHistory = async () => {
    if (!resourceId) return;

    setLoadingHistory(true);
    try {
      const history = await studyResourceStaffIssueService.getStaffLevelIssues(resourceId);
      setIssueHistory(Array.isArray(history) ? history : []);
    } catch (err) {
      setIssueHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load schools, roles, and history on mount
  useEffect(() => {
    if (!show || !resourceId) return;

    setLoadingSchools(true);
    ApiService.getAcademicSchools()
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setSchools(data);
      })
      .catch(() => setSchools([]))
      .finally(() => setLoadingSchools(false));

    setLoadingRoles(true);
    studyResourceStaffIssueService.getRoles()
      .then((rolesData) => {
        setRoles(rolesData);
      })
      .catch(() => setRoles([]))
      .finally(() => setLoadingRoles(false));

    loadHistory();
  }, [show, resourceId]);

  // Load users when both school and role are chosen
  useEffect(() => {
    if (!selectedRoleId || !selectedSchoolId) {
      setUsers([]);
      setSelectedUserIds([]);
      return;
    }

    setLoadingUsers(true);
    studyResourceStaffIssueService
      .getUsersByRole(Number(selectedRoleId), Number(selectedSchoolId))
      .then((usersData) => {
        setUsers(usersData);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [selectedRoleId, selectedSchoolId]);

  // User multi-select helpers
  const toggleUser = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map((u) => u.userId));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!selectedSchoolId) {
      setMessage({ type: 'error', text: 'Please select a School' });
      return;
    }
    if (!selectedRoleId) {
      setMessage({ type: 'error', text: 'Please select a Role' });
      return;
    }
    if (selectedUserIds.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one User' });
      return;
    }
    if (!visibleTo) {
      setMessage({ type: 'error', text: 'Please select Visible To date' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await studyResourceStaffIssueService.createStaffLevelIssue({
        resourceId: resourceId!,
        schoolId: selectedSchoolId ? Number(selectedSchoolId) : undefined,
        roleId: selectedRoleId ? Number(selectedRoleId) : undefined,
        userIds: selectedUserIds,
        startDate: visibleFrom,
        endDate: visibleTo,
      });

      setLoading(false);
      setMessage({ type: 'success', text: 'Resource issued successfully to staff' });
      resetForm(false); // Don't clear the success message
      loadHistory(); // Reload history after successful issue
    } catch (err: any) {
      setLoading(false);
      setMessage({ type: 'error', text: err.message || 'Failed to issue resource' });
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;

    setDeleting(true);
    try {
      await studyResourceStaffIssueService.deleteStaffLevelIssue(deleteConfirmId);
      setMessage({ type: 'success', text: 'Issue deleted successfully' });
      setDeleteConfirmId(null);
      loadHistory(); // Reload history after deletion
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to delete issue' });
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = (clearMessage = true) => {
    setSelectedSchoolId('');
    setSelectedRoleId('');
    setSelectedUserIds([]);
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    setVisibleFrom(now.toISOString().slice(0, 16));
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    setVisibleTo(d.toISOString().slice(0, 16));
    if (clearMessage) {
      setMessage(null);
    }
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (!resourceId) return null;

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          Staff Level Issue - {resourceName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Issue Form Card */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <span>Issue</span>
            </div>
            <div style={{ padding: 20 }}>
              <form onSubmit={handleSubmit}>
                {/* Cascading Dropdowns — 2-column grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  {/* School */}
                  <div>
                    <label style={labelStyle}>
                      School <span style={{ color: 'var(--status-danger)' }}>*</span>
                    </label>
                    <select
                      value={selectedSchoolId}
                      onChange={(e) => setSelectedSchoolId(e.target.value ? Number(e.target.value) : '')}
                      style={selectStyle}
                      required
                    >
                      <option value="">-- Select School --</option>
                      {schools.map((s) => (
                        <option key={s.schoolId} value={s.schoolId}>
                          {s.schoolName}
                        </option>
                      ))}
                    </select>
                    {loadingSchools && <span style={loadingHint}>Loading...</span>}
                  </div>

                  {/* Role */}
                  <div>
                    <label style={labelStyle}>
                      Role <span style={{ color: 'var(--status-danger)' }}>*</span>
                    </label>
                    <select
                      value={selectedRoleId}
                      onChange={(e) => setSelectedRoleId(e.target.value ? Number(e.target.value) : '')}
                      style={selectStyle}
                      required
                    >
                      <option value="">-- Select Role --</option>
                      {roles.map((r) => (
                        <option key={r.roleId} value={r.roleId}>
                          {r.roleName}
                        </option>
                      ))}
                    </select>
                    {loadingRoles && <span style={loadingHint}>Loading...</span>}
                  </div>
                </div>

                {/* Users - Multi-select checkbox list (full width) */}
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>
                    Users <span style={{ color: 'var(--status-danger)' }}>*</span>{' '}
                    {selectedUserIds.length > 0 && (
                      <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({selectedUserIds.length} selected)</span>
                    )}
                  </label>
                  {loadingUsers ? (
                    <div style={{ padding: 8, color: 'var(--text-muted)', fontSize: 13 }}>Loading users...</div>
                  ) : !selectedSchoolId || !selectedRoleId ? (
                    <div style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text-faint)', border: '1px solid var(--border-strong)', borderRadius: 6, background: 'var(--surface-page)' }}>
                      Select a school and role first
                    </div>
                  ) : users.length === 0 ? (
                    <div style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text-faint)', border: '1px solid var(--border-strong)', borderRadius: 6, background: 'var(--surface-page)' }}>
                      No users found for this role
                    </div>
                  ) : (
                    <div style={{ border: '1px solid var(--border-strong)', borderRadius: 6, maxHeight: 160, overflowY: 'auto', padding: 8 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontWeight: 600, fontSize: 13, borderBottom: '1px solid var(--border-default)', marginBottom: 4 }}>
                        <input
                          type="checkbox"
                          checked={selectedUserIds.length === users.length && users.length > 0}
                          onChange={handleSelectAllUsers}
                        />
                        Select All ({users.length})
                      </label>
                      {users.map((u) => (
                        <label key={u.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 13 }}>
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(u.userId)}
                            onChange={() => toggleUser(u.userId)}
                          />
                          {u.userName}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dates — 2-column grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  {/* Visible From */}
                  <div>
                    <label style={labelStyle}>
                      Visible From <span style={{ color: 'var(--status-danger)' }}>*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={visibleFrom}
                      onChange={(e) => setVisibleFrom(e.target.value)}
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Visible To */}
                  <div>
                    <label style={labelStyle}>
                      Visible To <span style={{ color: 'var(--status-danger)' }}>*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={visibleTo}
                      onChange={(e) => setVisibleTo(e.target.value)}
                      style={inputStyle}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <Button variant="secondary" onClick={handleClose} disabled={loading}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? 'Issuing...' : 'Issue'}
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Success/Error Message - displayed above history */}
          {message && (
            <div
              style={{
                padding: '12px 16px',
                borderRadius: 6,
                backgroundColor: message.type === 'success' ? '#ecfdf5' : '#fef2f2',
                color: message.type === 'success' ? '#065f46' : '#991b1b',
                border: `1px solid ${message.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
                fontSize: 14,
                fontWeight: 500,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>{message.text}</span>
              <button
                onClick={() => setMessage(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 18,
                  fontWeight: 'bold',
                  color: 'inherit',
                  padding: '0 4px',
                }}
              >
                ×
              </button>
            </div>
          )}

          {/* Issue History Card */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <span>Issue History</span>
              <button
                onClick={loadHistory}
                disabled={loadingHistory}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--brand-primary)',
                  cursor: loadingHistory ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  textDecoration: 'underline',
                }}
              >
                {loadingHistory ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            <div style={{ padding: 20 }}>
              {loadingHistory && issueHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>
                  Loading issue history...
                </div>
              ) : issueHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>
                  No issues found. Issue this resource to create history.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border-default)', textAlign: 'left' }}>
                        <th style={thStyle}>School</th>
                        <th style={thStyle}>Role</th>
                        <th style={thStyle}>User</th>
                        <th style={thStyle}>Visible From</th>
                        <th style={thStyle}>Visible To</th>
                        <th style={thStyle}>Issued On</th>
                        <th style={thStyle}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {issueHistory.map((issue: any) => (
                        <tr key={issue.Id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          <td style={tdStyle}>{issue.schoolName || '-'}</td>
                          <td style={tdStyle}>{issue.roleName || '-'}</td>
                          <td style={tdStyle}>{issue.userName || '-'}</td>
                          <td style={tdStyle}>{formatDate(issue.StartDate)}</td>
                          <td style={tdStyle}>{formatDate(issue.EndDate)}</td>
                          <td style={tdStyle}>{formatDate(issue.CreationDate)}</td>
                          <td style={tdStyle}>
                            <button
                              onClick={() => setDeleteConfirmId(issue.Id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--status-danger)',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                fontSize: 13,
                              }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteConfirmId} onHide={() => setDeleteConfirmId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this issue?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteConfirmId(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

// Styles
const cardStyle: React.CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 8,
  overflow: 'hidden',
  backgroundColor: 'var(--surface-card)',
};

const cardHeaderStyle: React.CSSProperties = {
  padding: '12px 20px',
  backgroundColor: 'var(--surface-page)',
  borderBottom: '1px solid var(--border-default)',
  fontWeight: 600,
  fontSize: 14,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 6,
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text-body)',
};

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 14,
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  backgroundColor: 'var(--surface-card)',
  cursor: 'pointer',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 14,
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
};

const loadingHint: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: 'var(--text-muted)',
  marginTop: 4,
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--text-body)',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  color: 'var(--text-muted)',
};

export default StudyResourceStaffIssueModal;
